/**
 * Product Personalizer v4 — Shopify Block Script
 *
 * Integrates into the product form by injecting hidden inputs
 * with name="properties[...]". When the customer clicks the
 * theme's add-to-cart button (or any app button like ReelAsIt),
 * the personalization data travels with the form automatically.
 *
 * Sections shown/hidden based on config:
 *   - colors: shown if config.colors has items
 *   - fonts: shown if config.fonts has 2+ items
 *   - text: shown if config.text.lineCount > 0
 *   - upload: shown if config.text.imageArea exists
 */

(function() {
  'use strict';

  // JPEG no tiene transparencia: lo transparente se vuelve NEGRO al exportar. Antes de
  // exportar se calca el canvas sobre blanco (si ya era opaco, no cambia nada).
  // Bug real 05/08/2026: los fondos con transparencia del Tag de Mascota se guardaban oscuros.
  // La placa troquelada NO maneja tildes (el troquel no tiene esos caracteres): se quitan
  // en la VISTA PREVIA y en las properties — así el cliente ve exactamente lo que se va a
  // troquelar, sin pelearle al campo mientras escribe. 🔴 La Ñ SÍ se queda (PEÑA no puede
  // volverse PENA); solo caen los acentos y la diéresis. Pedido de Toto, 05/08/2026.
  var __pzMapaTildes = { 'á':'a','é':'e','í':'i','ó':'o','ú':'u','ü':'u','à':'a','è':'e','ì':'i','ò':'o','ù':'u',
                         'Á':'A','É':'E','Í':'I','Ó':'O','Ú':'U','Ü':'U','À':'A','È':'E','Ì':'I','Ò':'O','Ù':'U' };
  function __pzSinTildes(s) {
    return String(s || '').replace(/[áéíóúüàèìòùÁÉÍÓÚÜÀÈÌÒÙ]/g, function (c) { return __pzMapaTildes[c] || c; });
  }

  function __pzSobreBlanco(cv) {
    var c = document.createElement('canvas');
    c.width = cv.width; c.height = cv.height;
    var x = c.getContext('2d');
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, c.width, c.height);
    x.drawImage(cv, 0, 0);
    return c;
  }


  class ProductPersonalizer {
    constructor(el) {
      this.el = el;
      this.config = null;
      this.images = {};
      this.currentImg = null;
      this.customerImg = null;
      this.productForm = null;

      // Canvas (optional — only if preview toggle exists)
      this.canvas = el.querySelector('[data-pz-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

      this.state = {
        color: null,
        lines: [],
        maxChars: [],
        required: [],
        font: null,
        variantId: null
      };

      this._init();
    }

    /* ============================================
       INIT
       ============================================ */
    _init() {
      try {
        this.config = JSON.parse(this.el.dataset.pzConfig);
      } catch(e) {
        console.error('[PZ] Invalid config:', e);
        return;
      }

      // Skip if this is a dual config — handled by product-personalizer-dual.js
      if (this.config.type === 'dual') {
        this.el.style.display = 'none';
        return;
      }

      const txt = this.config.text || {};
      const lineCount = txt.lineCount || 0;

      // State
      this.state.lines = new Array(lineCount).fill('');
      this.state.maxChars = txt.maxChars || new Array(lineCount).fill(14);
      this.state.required = txt.required || [];
      this.state.variantId = this.el.dataset.pzVariantId || null;

      // Variantes de Shopify (expuestas por el liquid) — stock real por color
      try { this.shopifyVariants = JSON.parse(this.el.dataset.pzVariants || '[]'); } catch (e) { this.shopifyVariants = []; }
      try { this.shopifyOptions = JSON.parse(this.el.dataset.pzOptions || '[]'); } catch (e) { this.shopifyOptions = []; }
      this.productAvailable = this.el.dataset.pzProductAvailable === 'true';
      this.variantMap = {};
      this._buildVariantMap();

      // First available color (preferir uno con stock real)
      const colors = this.config.colors || [];
      let firstColor = colors.find(c => c.available !== false && this._isColorAvailable(c.name));
      if (!firstColor) firstColor = colors.find(c => c.available !== false);
      this.state.color = firstColor ? firstColor.id : null;

      // Font (1 sola fuente en single mode; si no hay configurada, fallback a ElegantTypewriter)
      const fonts = this.config.fonts || [];
      const hasFontFamily = fonts.length > 0 && fonts[0].family;
      this.state.font = hasFontFamily ? fonts[0].family : 'ElegantTypewriter';

      // Cargar fuente custom vía FontFace si está configurada con URL
      this._loadCustomFont();

      // Apply per-product styles
      this._applyStyles();

      // Determine which sections to show
      this._showSections();

      // Render visible sections
      if (this._sectionVisible('colors')) this._renderColors();
      if (this._sectionVisible('fonts'))  this._renderFontSelector();
      if (this._sectionVisible('text'))   this._renderFields();
      if (this._sectionVisible('upload')) this._setupUpload();

      // Update labels from config
      this._updateLabels();

      // Find the product form and set up integration
      this.productForm = this._findProductForm();
      if (this.productForm) {
        this._injectHiddenInputs();
        this._interceptFormSubmit();
      } else {
        console.warn('[PZ] Product form not found. Will try intercepting fetch/XHR.');
      }

      // Interceptar botón de ReelAsIt (validar ANTES de que abra el modal)
      this._interceptReelAsItButton();

      // Intercept AJAX add-to-cart (covers apps like ReelAsIt and AJAX themes)
      this._interceptFetchAddToCart();
      this._interceptXHRAddToCart();

      // Load images for canvas preview (if present)
      if (this.canvas) {
        this._loadImages();
      }

      // Bind events
      this._bindEvents();
      this._updateBadge();
      this._updateStatus();

      // Stock real de Shopify (solo si el sync está activo)
      this._hideThemeVariantSelectors();
      this._updateVariantId();
      this._checkGlobalStock();
    }

    /* ============================================
       SHOPIFY VARIANTS — stock real por color
       ============================================ */
    _buildVariantMap() {
      const colorOpt = (this.config.shopifyColorOption || 'Color').toLowerCase();
      const opts = this.shopifyOptions || [];
      (this.shopifyVariants || []).forEach(v => {
        let color = null;
        if (v.option1 !== undefined) {
          [v.option1, v.option2, v.option3].forEach((val, idx) => {
            if (!val || !opts[idx]) return;
            if ((opts[idx].name || '').toLowerCase() === colorOpt) color = val;
          });
        } else if (Array.isArray(v.options)) {
          v.options.forEach(o => {
            if ((o.name || '').toLowerCase() === colorOpt) color = o.value;
          });
        }
        if (color) this.variantMap[color] = { id: v.id, available: v.available !== false };
      });
    }

    // ¿Producto simple (sin variantes reales, solo "Default Title")?
    _isSimpleProduct() {
      const opts = this.shopifyOptions || [];
      if (opts.length === 0) return true;
      if (opts.length === 1 && (opts[0].name || '').toLowerCase() === 'title') return true;
      return false;
    }

    _isColorAvailable(colorName) {
      // Sync desactivado (default) → siempre disponible
      if (this.config.enableStockSync !== true) return true;
      // Producto simple (sin variantes) → usar el stock GLOBAL del producto
      if (this._isSimpleProduct()) return this.productAvailable;
      // Sync activo pero sin mapeo (nombres que no coinciden) → no bloquear (se avisa en el admin)
      if (Object.keys(this.variantMap).length === 0) return true;
      if (!colorName) return false;
      const v = this.variantMap[colorName];
      return v ? v.available : false;
    }

    _getActiveColor() {
      return (this.config.colors || []).find(c => c.id === this.state.color) || null;
    }

    _updateVariantId() {
      if (this.config.enableStockSync !== true) return;
      const color = this._getActiveColor();
      if (!color) return;
      const v = this.variantMap[color.name];
      if (!v) return;
      this.state.variantId = v.id;
      if (this.productForm) {
        const idInput = this.productForm.querySelector('input[name="id"], select[name="id"]');
        if (idInput) {
          idInput.value = v.id;
          // Avisar al theme por si escucha el cambio (algunos actualizan el precio con esto)
          try { idInput.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
        }
      }
      this._injectHiddenInputs();
    }

    // Si es producto simple, el sync está activo y el stock global es 0 → aviso persistente.
    _checkGlobalStock() {
      if (this.config.enableStockSync === true && this._isSimpleProduct() && !this.productAvailable) {
        const box = this.el.querySelector('[data-pz-msg="error"]');
        if (box) {
          box.textContent = 'Producto agotado temporalmente.';
          box.classList.add('is-visible');
        }
      }
    }

    _hideThemeVariantSelectors() {
      if (this.config.enableStockSync !== true) return;

      // Selectores de variantes del theme: suelen estar FUERA del form del producto.
      document.querySelectorAll(
        'input[name^="options"], input[name^="Options"], ' +
        'select[name^="options"], select[name^="Options"]'
      ).forEach(input => {
        const wrapper = input.closest(
          'fieldset, variant-radios, variant-selects, ' +
          '.product-form__input, .product-form__option, .product-options, ' +
          '.variant-input-wrap, .swatch, [data-product-option]'
        );
        if (wrapper) wrapper.style.display = 'none';
      });

      document.querySelectorAll(
        'variant-radios, variant-selects, ' +
        '.product-form__variants, .product-form__option-selectors, ' +
        '.product-info__variant-picker, [data-variant-selector], ' +
        '.vvp'  // theme Toto V.7 (Mistikal) - variant visual picker propio
      ).forEach(el => { el.style.display = 'none'; });
    }

    /* ============================================
       FIND PRODUCT FORM
       ============================================ */
    _findProductForm() {
      // Try using the form ID from the data attribute
      const formId = this.el.dataset.pzFormId;
      if (formId) {
        const form = document.getElementById(formId);
        if (form) return form;
      }

      // Fallback: look for the standard Shopify product form
      const selectors = [
        'form[action="/cart/add"]',
        'form[action^="/cart/add"]',
        'product-form form',
        'form.feature-product-form',
        'form[data-type="add-to-cart-form"]'
      ];

      for (const sel of selectors) {
        const form = document.querySelector(sel);
        if (form) return form;
      }

      return null;
    }

    /* ============================================
       HIDDEN INPUTS — inject properties into form
       ============================================ */
    _injectHiddenInputs() {
      // Container inside our block for the hidden inputs
      const container = this.el.querySelector('[data-pz-hidden-inputs]');
      if (!container) return;

      // Clear previous inputs
      container.innerHTML = '';

      const props = this._buildProperties();
      const formId = this.productForm ? this.productForm.id : null;

      for (const [key, value] of Object.entries(props)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = value;
        input.setAttribute('data-pz-property', '');
        // Associate with the product form via form attribute
        if (formId) {
          input.setAttribute('form', formId);
        }
        container.appendChild(input);
      }
    }

    /* ============================================
       BUILD PROPERTIES — extract personalization data
       ============================================ */
    _buildProperties() {
      const props = {};
      const cl = this.config.cartLabels || {};

      // Color (only if colors section is visible)
      if (this._sectionVisible('colors')) {
        const colorCfg = (this.config.colors || []).find(c => c.id === this.state.color);
        const colorKey = cl.color || this.config.colorLabel || 'Color';
        props[colorKey] = colorCfg ? colorCfg.name : this.state.color;
      }

      // Font (only if fonts section is visible)
      if (this._sectionVisible('fonts')) {
        const f = (this.config.fonts || []).find(f => f.family === this.state.font);
        const fontKey = cl.font || 'Tipografía';
        props[fontKey] = f ? (f.label || f.family) : this.state.font;
      }

      // Text lines
      const labels = (this.config.text || {}).labels || [];
      const lineLabels = cl.lineLabels || [];
      for (let i = 0; i < this.state.lines.length; i++) {
        if (this.state.lines[i].trim()) {
          const key = lineLabels[i] || labels[i] || ('Línea ' + (i + 1));
          props[key] = __pzSinTildes(this.state.lines[i]).toUpperCase();
        }
      }

      // Customer image flag
      if (this.customerImg) {
        props['_imagen_cliente'] = 'Si';
      }

      // Código único para el botón "Ver mi diseño" del carrito (derivado del preview).
      // Se pega a la ÚLTIMA propiedad visible → el botón queda al FINAL de la lista.
      if (this._previewUrl) {
        const _cref = String(this._previewUrl).replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
        const _cvis = Object.keys(props).filter(k => k.charAt(0) !== '_');
        if (_cref && _cvis.length) {
          props[_cvis[_cvis.length - 1]] += ' (#' + _cref + ')';
          props['_ref'] = _cref;
        }
      }

      return props;
    }

    /* ============================================
       INTERCEPT FORM SUBMIT — validate before submit
       ============================================ */
    _interceptFormSubmit() {
      if (!this.productForm) return;
      var self = this;

      this.productForm.addEventListener('submit', function(e) {
        // Re-inject hidden inputs con el estado actual
        self._injectHiddenInputs();

        // Inyectar preview URL si ya la tenemos
        if (self._previewUrl) {
          self._injectPreviewInput(self._previewUrl);
        }

        // Validar
        if (!self._validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }

        // Válido — dejar que el form se envíe con los hidden inputs
      }, true); // useCapture: true para correr antes del handler del tema
    }

    /* ============================================
       INTERCEPT FETCH — for AJAX add-to-cart
       ============================================ */
    _interceptFetchAddToCart() {
      var self = this;
      var originalFetch = window.fetch;

      window.fetch = function(url, options) {
        var fetchCtx = this;
        var urlStr = typeof url === 'string' ? url : (url instanceof Request ? url.url : '');
        if (!urlStr.includes('/cart/add')) {
          return originalFetch.apply(fetchCtx, arguments);
        }

        if (!options || !options.body) {
          return originalFetch.apply(fetchCtx, arguments);
        }

        // Validate before allowing add-to-cart
        self._injectHiddenInputs();
        if (!self._validate()) {
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return Promise.reject(new Error('Personalización incompleta'));
        }

        // Inyectar properties (incluyendo preview URL si ya la tenemos)
        var pzProps = self._buildProperties();
        if (self._previewUrl) {
          pzProps['_Vista Previa'] = self._previewUrl;
        }
        if (options.body instanceof FormData) {
          for (var key in pzProps) {
            options.body.set('properties[' + key + ']', pzProps[key]);
          }
        } else if (typeof options.body === 'string') {
          var injected = false;

          // Intentar JSON primero
          try {
            var data = JSON.parse(options.body);
            if (typeof data === 'object' && data !== null) {
              data.properties = Object.assign({}, data.properties || {}, pzProps);
              options = Object.assign({}, options, { body: JSON.stringify(data) });
              injected = true;
            }
          } catch(err) {}

          // Si no era JSON, intentar URL-encoded (key=val&key=val)
          if (!injected && options.body.indexOf('=') !== -1) {
            var params = new URLSearchParams(options.body);
            for (var key in pzProps) {
              params.set('properties[' + key + ']', pzProps[key]);
            }
            options = Object.assign({}, options, { body: params.toString() });
          }
        }

        return originalFetch.apply(fetchCtx, [url, options]);
      };
    }

    /* ============================================
       INTERCEPT XMLHttpRequest — for apps using XHR
       ============================================ */
    _interceptXHRAddToCart() {
      var self = this;
      var originalOpen = XMLHttpRequest.prototype.open;
      var originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function(method, url) {
        this._pzUrl = typeof url === 'string' ? url : '';
        this._pzMethod = method;
        return originalOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function(body) {
        if (!this._pzUrl || !this._pzUrl.includes('/cart/add')) {
          return originalSend.apply(this, arguments);
        }

        if (!body) {
          return originalSend.apply(this, arguments);
        }

        // Validate before allowing add-to-cart
        self._injectHiddenInputs();
        if (!self._validate()) {
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.abort();
          return;
        }

        var pzProps = self._buildProperties();
        if (self._previewUrl) {
          pzProps['_Vista Previa'] = self._previewUrl;
        }
        // FormData
        if (body instanceof FormData) {
          for (var key in pzProps) {
            body.set('properties[' + key + ']', pzProps[key]);
          }
          return originalSend.apply(this, [body]);
        }

        // String body (JSON or URL-encoded)
        if (typeof body === 'string') {
          var injected = false;

          // JSON
          try {
            var data = JSON.parse(body);
            if (typeof data === 'object' && data !== null) {
              data.properties = Object.assign({}, data.properties || {}, pzProps);
              body = JSON.stringify(data);
              injected = true;
            }
          } catch(err) {}

          // URL-encoded
          if (!injected && body.indexOf('=') !== -1) {
            var params = new URLSearchParams(body);
            for (var key in pzProps) {
              params.set('properties[' + key + ']', pzProps[key]);
            }
            body = params.toString();
          }
        }

        return originalSend.apply(this, [body]);
      };
    }

    /* ============================================
       SECTION VISIBILITY
       ============================================ */
    _showSections() {
      const txt = this.config.text || {};
      const colors = this.config.colors || [];
      const fonts = this.config.fonts || [];

      const rules = {
        colors: colors.length > 0,
        fonts:  fonts.length > 1,
        text:   (txt.lineCount || 0) > 0,
        upload: !!txt.imageArea
      };

      // Show sections that pass rules
      const visibleSections = [];
      for (const [name, visible] of Object.entries(rules)) {
        const section = this.el.querySelector('[data-pz-section="' + name + '"]');
        if (!section) continue;
        if (visible) {
          section.style.display = '';
          visibleSections.push(section);
        }
      }

      // Remove trailing divider from the LAST visible section
      if (visibleSections.length > 0) {
        const lastSection = visibleSections[visibleSections.length - 1];
        const lastDivider = lastSection.querySelector('.pz__divider');
        if (lastDivider) lastDivider.style.display = 'none';
      }
    }

    _sectionVisible(name) {
      const section = this.el.querySelector('[data-pz-section="' + name + '"]');
      return section && section.style.display !== 'none';
    }

    /* ============================================
       UPDATE LABELS FROM CONFIG
       ============================================ */
    _updateLabels() {
      // Color label (e.g. "Color Hule" instead of just "Color")
      if (this.config.colorLabel) {
        const lbl = this.el.querySelector('[data-pz-color-label]');
        if (lbl) {
          const badge = lbl.querySelector('[data-pz-color-name]');
          lbl.textContent = this.config.colorLabel + ' ';
          if (badge) lbl.appendChild(badge);
        }
      }

      // Text section label
      const txt = this.config.text || {};
      if (txt.sectionLabel) {
        const lbl = this.el.querySelector('[data-pz-text-label]');
        if (lbl) lbl.textContent = txt.sectionLabel;
      }

      // Upload label
      if (txt.uploadLabel) {
        const lbl = this.el.querySelector('[data-pz-upload-label]');
        if (lbl) lbl.textContent = txt.uploadLabel;
      }

      // Preview label
      if (this.config.previewLabel) {
        const lbl = this.el.querySelector('[data-pz-preview-label]');
        if (lbl) lbl.textContent = this.config.previewLabel;
      }

      // Preview hint
      if (this.config.previewHint !== undefined) {
        const hint = this.el.querySelector('[data-pz-preview-hint]');
        if (hint) hint.textContent = this.config.previewHint ? '(' + this.config.previewHint + ')' : '';
      }
    }

    /* ============================================
       PER-PRODUCT STYLES
       ============================================ */
    _applyStyles() {
      const s = this.config.styles || {};

      const map = {
        'accent':        s.accentColor,
        'accent-hover':  s.accentHover,
        'card-bg':       s.cardBg,
        'border':        s.borderColor,
        'input-bg':      s.inputBg,
        'text':          s.textColor,
        'text-muted':    s.mutedColor,
        'label-weight':  s.labelBold === false ? '500' : '700',
        'label-size':    s.labelSize,
        'input-size':    s.inputSize,
        'radius':        s.radius,
        'btn-bg':        s.btnBg,
        'btn-text':      s.btnText,
        'btn-radius':    s.btnRadius
      };

      for (const [key, val] of Object.entries(map)) {
        if (val != null && val !== '') {
          this.el.style.setProperty('--pz-' + key, val);
        }
      }
    }

    /* ============================================
       LOAD IMAGES (for canvas preview)
       ============================================ */
    _loadImages() {
      const colors = this.config.colors || [];
      const loading = this.el.querySelector('[data-pz-loading]');
      let pending = colors.filter(c => c.image).length;

      // If no colors have images, check for a single preview image
      if (pending === 0) {
        const previewImg = (this.config.text || {}).previewImage;
        if (previewImg) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            this.currentImg = img;
            this.canvas.width = img.naturalWidth;
            this.canvas.height = img.naturalHeight;
            this._drawPreview();
            if (loading) loading.classList.add('is-hidden');
          };
          img.onerror = () => {
            this._drawPlaceholder();
            if (loading) loading.classList.add('is-hidden');
          };
          img.src = previewImg;
          return;
        }

        this.canvas.width = 800;
        this.canvas.height = 800;
        this._drawPlaceholder();
        if (loading) loading.classList.add('is-hidden');
        return;
      }

      colors.forEach(c => {
        if (!c.image) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this.images[c.id] = img;
          pending--;
          if (c.id === this.state.color) {
            this.currentImg = img;
            this.canvas.width = img.naturalWidth;
            this.canvas.height = img.naturalHeight;
            this._drawPreview();
          }
          if (pending <= 0 && loading) loading.classList.add('is-hidden');
        };
        img.onerror = () => {
          console.warn('[PZ] Image failed:', c.id);
          pending--;
          if (pending <= 0 && loading) loading.classList.add('is-hidden');
        };
        img.src = c.image;
      });
    }

    /* ============================================
       DRAWING (canvas preview)
       ============================================ */
    _drawPreview() {
      if (!this.canvas || !this.ctx) return;
      if (!this.currentImg) return this._drawPlaceholder();

      const w = this.canvas.width;
      const h = this.canvas.height;

      this.ctx.clearRect(0, 0, w, h);
      this.ctx.drawImage(this.currentImg, 0, 0);

      if (this.customerImg && this.config.text && this.config.text.imageArea) {
        this._drawCustomerImg(w, h);
      }

      if (this.state.lines.length > 0) {
        this._drawText(w, h);
      }

      // Subir preview en background (con debounce)
      this._schedulePreviewUpload();
    }

    _drawPlaceholder() {
      if (!this.canvas || !this.ctx) return;
      const w = this.canvas.width;
      const h = this.canvas.height;
      this.ctx.fillStyle = '#e8e5e0';
      this.ctx.fillRect(0, 0, w, h);
      this.ctx.fillStyle = '#aaa';
      this.ctx.font = '16px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText('Vista previa', w / 2, h / 2);
    }

    _drawText(w, h) {
      const cfg = this.config.text || {};
      const fontSize = Math.round(w * (cfg.fontSize || 0.058));
      const family = this.state.font || 'ElegantTypewriter';

      this.ctx.save();
      const align = cfg.align || 'left';
      this.ctx.textAlign = align;
      this.ctx.textBaseline = 'middle';
      this.ctx.font = 'bold ' + fontSize + 'px \'' + family + '\', monospace';

      var x;
      if (align === 'left')        x = w * (cfg.leftX || 0.30);
      else if (align === 'center') x = w * (cfg.centerX || 0.52);
      else                         x = w * (cfg.rightX || 0.74);

      const shadowColor = cfg.shadowColor || 'rgba(255,255,255,0.3)';
      const sx = cfg.shadowOffsetX != null ? cfg.shadowOffsetX : 1;
      const sy = cfg.shadowOffsetY != null ? cfg.shadowOffsetY : 1;
      const textColor = cfg.color || 'rgba(50,45,35,0.88)';

      for (let i = 0; i < this.state.lines.length; i++) {
        const text = __pzSinTildes(this.state.lines[i]).toUpperCase();
        if (!text) continue;
        const y = h * (cfg.topY || 0.34) + i * (h * (cfg.lineHeight || 0.073));

        this.ctx.fillStyle = shadowColor;
        this.ctx.fillText(text, x + sx, y + sy);
        this.ctx.fillStyle = textColor;
        this.ctx.fillText(text, x, y);
      }

      this.ctx.restore();
    }

    _drawCustomerImg(w, h) {
      const a = this.config.text.imageArea;
      if (!a || !this.customerImg) return;
      this.ctx.drawImage(this.customerImg, w * a.x, h * a.y, w * a.width, h * a.height);
    }

    /* ============================================
       RENDER: COLORS
       ============================================ */
    _renderColors() {
      const container = this.el.querySelector('[data-pz-colors]');
      if (!container) return;

      container.innerHTML = '';
      // "Disponible" = habilitado en el admin Y con stock real en Shopify (si el sync está activo)
      const isAvail = (c) => c.available !== false && this._isColorAvailable(c.name);

      // Disponibles primero, agotados al final (sort estable: respeta el orden manual)
      const sortedColors = (this.config.colors || []).slice().sort((a, b) =>
        (isAvail(a) ? 0 : 1) - (isAvail(b) ? 0 : 1));
      sortedColors.forEach(c => {
        const soldOut = !isAvail(c);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pz__color-btn';
        if (this.config.colorSize) {
          btn.style.setProperty('--pz-color-size', this.config.colorSize + 'px');
        }
        if (this.state.color === c.id) btn.classList.add('is-active');
        if (soldOut) btn.classList.add('is-unavailable');
        btn.dataset.colorId = c.id;
        btn.setAttribute('aria-label', c.name + (soldOut ? ' (Agotado)' : ''));

        // Si tiene imagen de thumbnail, mostrar imagen; si no, color sólido
        var innerHtml;
        if (c.thumbnail) {
          innerHtml = '<img class="pz__color-img" src="' + c.thumbnail + '" alt="' + c.name + '">';
        } else {
          innerHtml = '<div class="pz__color-inner" style="background:' + c.hex + '"></div>';
        }

        btn.innerHTML =
          innerHtml +
          '<span class="pz__color-name">' + c.name + (soldOut ? ' (Agotado)' : '') + '</span>';
        container.appendChild(btn);
      });
    }

    /* ============================================
       RENDER: TEXT FIELDS
       ============================================ */
    _renderFields() {
      const container = this.el.querySelector('[data-pz-fields]');
      if (!container) return;

      const txt = this.config.text || {};
      const labels = txt.labels || [];
      const lineCount = this.state.lines.length;
      const placeholder = txt.placeholder || 'Escribe aquí...';

      container.innerHTML = '';
      for (let i = 0; i < lineCount; i++) {
        const maxLen = this.state.maxChars[i] || 14;
        const isRequired = this.state.required.includes(i + 1);
        const label = labels[i] || ('Linea ' + (i + 1));

        const div = document.createElement('div');
        div.className = 'pz__field';
        div.innerHTML =
          '<div class="pz__field-header">' +
            '<span class="pz__field-label">' + label + (isRequired ? '<span class="pz__required-dot"> *</span>' : '') + '</span>' +
            '<span class="pz__char-count" data-pz-count="' + i + '">' + maxLen + ' restantes</span>' +
          '</div>' +
          '<input type="text" class="pz__input" data-pz-line="' + i + '" maxlength="' + maxLen + '" placeholder="' + placeholder + '" autocomplete="off">';
        container.appendChild(div);
      }
    }

    /* ============================================
       RENDER: FONT SELECTOR
       ============================================ */
    _renderFontSelector() {
      const container = this.el.querySelector('[data-pz-font-select]');
      if (!container) return;

      const fonts = this.config.fonts || [];
      container.innerHTML = '';
      fonts.forEach(f => {
        const opt = document.createElement('option');
        opt.value = f.family;
        opt.textContent = f.label || f.family;
        if (f.family === this.state.font) opt.selected = true;
        container.appendChild(opt);
      });
    }

    /* ============================================
       LOAD CUSTOM FONT
       Si el admin configuró una fuente custom con URL,
       la carga con FontFace API. Si no, queda el fallback
       ElegantTypewriter que precarga el liquid.
       ============================================ */
    _loadCustomFont() {
      const fonts = this.config.fonts || [];
      if (fonts.length === 0) return;
      const f = fonts[0];
      if (!f || !f.family || !f.src) return;
      // No recargar si ya fue agregada (cache simple por family)
      if (document.fonts && document.fonts.check('16px "' + f.family + '"')) return;
      try {
        const ff = new FontFace(f.family, 'url(' + f.src + ')');
        ff.load().then((loaded) => {
          document.fonts.add(loaded);
          // Redibujar las líneas del canvas que dependen de la fuente
          if (typeof this._drawAll === 'function') this._drawAll();
        }).catch(() => {});
      } catch (e) {}
    }

    /* ============================================
       SETUP: IMAGE UPLOAD
       ============================================ */
    _setupUpload() {
      // Section is already visible from _showSections
    }

    /* ============================================
       EVENTS
       ============================================ */
    _bindEvents() {
      // Color click
      this.el.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-color-id]');
        if (btn && !btn.classList.contains('is-unavailable')) {
          this._selectColor(btn.dataset.colorId);
        }
      });

      // Text input
      this.el.addEventListener('input', (e) => {
        if (e.target.hasAttribute('data-pz-line')) {
          this._onInput(e.target);
        }
      });

      // Font change
      const fontSel = this.el.querySelector('[data-pz-font-select]');
      if (fontSel) {
        fontSel.addEventListener('change', (e) => {
          this.state.font = e.target.value;
          this._drawPreview();
          this._injectHiddenInputs();
        });
      }

      // Image upload
      const uploadInput = this.el.querySelector('[data-pz-upload-input]');
      const uploadArea = this.el.querySelector('[data-pz-upload-area]');
      if (uploadInput && uploadArea) {
        uploadArea.addEventListener('click', () => uploadInput.click());
        uploadInput.addEventListener('change', (e) => this._onUpload(e));
      }

      // Remove uploaded image
      this.el.addEventListener('click', (e) => {
        if (e.target.closest('[data-pz-upload-remove]')) this._removeUpload();
      });
    }

    _selectColor(id) {
      // No permitir elegir un color agotado
      const target = (this.config.colors || []).find(c => c.id === id);
      if (target && (target.available === false || !this._isColorAvailable(target.name))) return;

      this.state.color = id;
      this.el.querySelectorAll('[data-color-id]').forEach(b =>
        b.classList.toggle('is-active', b.dataset.colorId === id)
      );
      this._updateBadge();
      this._updateVariantId();

      if (this.canvas && this.images[id]) {
        this.currentImg = this.images[id];
        this.canvas.width = this.currentImg.naturalWidth;
        this.canvas.height = this.currentImg.naturalHeight;
      }
      this._drawPreview();
      this._injectHiddenInputs();
    }

    _updateBadge() {
      const badge = this.el.querySelector('[data-pz-color-name]');
      if (!badge) return;
      const c = (this.config.colors || []).find(c => c.id === this.state.color);
      badge.textContent = c ? c.name : '';
    }

    // Remueve emojis (preserva ♥ U+2665, que es grabable).
    _stripEmojis(str) {
      if (!str) return str;
      return str.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{2664}\u{2666}-\u{27BF}\u{2300}-\u{23FF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{200D}\u{20E3}\u{2B00}-\u{2BFF}]/gu, '');
    }

    _onInput(input) {
      const i = parseInt(input.dataset.pzLine, 10);
      // Bloquear emojis si el admin los prohibió (preserva ♥)
      if (this.config.allowEmojis === false) {
        const cleaned = this._stripEmojis(input.value);
        if (cleaned !== input.value) input.value = cleaned;
      }
      const max = this.state.maxChars[i] || 14;
      const remaining = max - input.value.length;

      this.state.lines[i] = input.value;
      input.classList.remove('is-error');

      const counter = this.el.querySelector('[data-pz-count="' + i + '"]');
      if (counter) {
        counter.textContent = remaining + ' restantes';
        counter.classList.remove('is-warning', 'is-full');
        if (remaining === 0) counter.classList.add('is-full');
        else if (remaining <= 3) counter.classList.add('is-warning');
      }

      this._drawPreview();
      this._injectHiddenInputs();
      this._updateStatus();
    }

    /* ============================================
       IMAGE UPLOAD
       ============================================ */
    _onUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this._showMsg('error', 'Por favor sube una imagen (JPG, PNG).');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this._showMsg('error', 'La imagen no debe pesar más de 10 MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          this.customerImg = img;
          this._drawPreview();
          this._injectHiddenInputs();
          this._updateStatus();
          const area = this.el.querySelector('[data-pz-upload-area]');
          if (area) {
            area.classList.add('has-image');
            area.innerHTML =
              '<img src="' + ev.target.result + '" class="pz__upload-preview" alt="Tu imagen">' +
              '<button type="button" class="pz__upload-remove" data-pz-upload-remove>' +
                '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
                ' Quitar imagen' +
              '</button>';
          }
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    }

    _removeUpload() {
      this.customerImg = null;
      const area = this.el.querySelector('[data-pz-upload-area]');
      const input = this.el.querySelector('[data-pz-upload-input]');
      if (area) {
        area.classList.remove('has-image');
        area.innerHTML =
          '<svg class="pz__upload-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>' +
          '<div class="pz__upload-text">Haz clic para subir tu imagen</div>';
      }
      if (input) input.value = '';
      this._drawPreview();
      this._injectHiddenInputs();
      this._updateStatus();
    }

    /* ============================================
       PREVIEW UPLOAD — sube en background con debounce
       Se llama automáticamente cada vez que el canvas
       cambia. Cuando el cliente da click en "agregar",
       la URL ya está lista (this._previewUrl).
       ============================================ */
    _schedulePreviewUpload() {
      // Debounce: esperar 1.5s de inactividad antes de subir
      if (this._uploadTimer) clearTimeout(this._uploadTimer);
      var self = this;
      this._uploadTimer = setTimeout(function() {
        self._doUploadPreview();
      }, 1500);
    }

    async _doUploadPreview() {
      if (!this.canvas || !this.currentImg) return;

      var uploadUrl = this.el.dataset.pzUploadUrl;
      var uploadKey = this.el.dataset.pzUploadKey;
      if (!uploadUrl || !uploadKey) return;

      try {
        var dataUrl = __pzSobreBlanco(this.canvas).toDataURL('image/jpeg', 0.8);

        var controller = new AbortController();
        var timeoutId = setTimeout(function() { controller.abort(); }, 15000);

        var response = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Upload-Key': uploadKey
          },
          body: JSON.stringify({
            image: dataUrl,
            productId: this.el.dataset.pzVariantId || '',
            variantId: this.state.variantId || ''
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          var result = await response.json();
          if (result.url) {
            this._previewUrl = result.url;
            // Inyectar de una vez como hidden input
            this._injectPreviewInput(result.url);
          }
        } else {
          console.warn('[PZ] Preview upload failed:', response.status);
        }
      } catch (err) {
        console.warn('[PZ] Preview upload error:', err);
      }
    }

    _injectPreviewInput(url) {
      var container = this.el.querySelector('[data-pz-hidden-inputs]');
      if (!container) return;

      var existing = container.querySelector('[data-pz-preview-input]');
      if (existing) existing.remove();

      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_Vista Previa]';
      input.value = url;
      input.setAttribute('data-pz-preview-input', '');
      if (this.productForm && this.productForm.id) {
        input.setAttribute('form', this.productForm.id);
      }
      container.appendChild(input);
    }

    /* ============================================
       INTERCEPT REELAIST BUTTON — validar antes del modal
       ============================================ */
    _interceptReelAsItButton() {
      var self = this;

      // ReelAsIt usa botones con clase _rsi-buy-now-button
      // Interceptamos click en capture phase (antes que ReelAsIt)
      document.addEventListener('click', function(e) {
        var rsiBtn = e.target.closest('._rsi-buy-now-button, [class*="_rsi-buy-now"]');
        if (!rsiBtn) return;

        // Si el click viene de DENTRO del modal de ReelAsIt (el botón de Confirmar),
        // dejarlo pasar — la validación ya se hizo al abrir el modal
        var insideModal = e.target.closest('#_rsi-cod-form-modal');
        if (insideModal) return;

        // Si el botón NO está en la misma sección de producto que el personalizador,
        // dejarlo pasar (ej: botón de ReelAsIt en el slider/drawer del carrito)
        var productSection = self.el.closest('[data-section-id], .shopify-section, section');
        if (productSection && !productSection.contains(rsiBtn)) return;

        // Validar personalización ANTES de que ReelAsIt abra el modal
        if (!self._validate()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true); // capture: true = corre antes que los listeners de ReelAsIt
    }

    /* ============================================
       STATUS INDICATOR — real-time feedback
       ============================================ */
    _updateStatus() {
      var statusEl = this.el.querySelector('[data-pz-status]');
      if (!statusEl) return;

      // If no required fields and no required image, don't show status
      var hasRequiredFields = this.state.required.length > 0;
      var hasRequiredImage = this.config.text && this.config.text.imageRequired;
      if (!hasRequiredFields && !hasRequiredImage) {
        statusEl.classList.remove('is-visible');
        return;
      }

      var missing = [];
      var labels = (this.config.text || {}).labels || [];

      // Check required text fields
      for (var r = 0; r < this.state.required.length; r++) {
        var lineNum = this.state.required[r];
        var idx = lineNum - 1;
        if (idx < 0 || idx >= this.state.lines.length) continue;
        if (!this.state.lines[idx].trim()) {
          missing.push(labels[idx] || ('Linea ' + lineNum));
        }
      }

      // Check required image
      if (hasRequiredImage && !this.customerImg) {
        missing.push('imagen');
      }

      statusEl.classList.add('is-visible');
      statusEl.classList.remove('is-pending', 'is-ready');

      if (missing.length > 0) {
        statusEl.classList.add('is-pending');
        statusEl.textContent = 'Pendiente: ' + missing.join(', ');
      } else {
        statusEl.classList.add('is-ready');
        statusEl.textContent = '\u2713\u2002Listo \u2014 puedes hacer tu pedido';
      }
    }

    /* ============================================
       VALIDATION
       ============================================ */
    _validate() {
      this._hideMsg();
      var valid = true;
      var missing = [];

      // Stock real de Shopify (solo si el sync está activo)
      if (this.config.enableStockSync === true) {
        // Producto simple agotado (stock global) → bloquear todo con un mensaje claro.
        if (this._isSimpleProduct() && !this.productAvailable) {
          this._showMsg('error', 'Este producto está agotado.');
          return false;
        }
        var stColor = this._getActiveColor();
        if (stColor && !this._isColorAvailable(stColor.name)) {
          this._showMsg('error', 'Este color está agotado. Elegí otro.');
          return false;
        }
      }

      // Check required fields
      for (var r = 0; r < this.state.required.length; r++) {
        var lineNum = this.state.required[r];
        var idx = lineNum - 1;
        if (idx < 0 || idx >= this.state.lines.length) continue;
        if (!this.state.lines[idx].trim()) {
          valid = false;
          var labels = (this.config.text || {}).labels || [];
          missing.push(labels[idx] || ('Linea ' + lineNum));
          var input = this.el.querySelector('[data-pz-line="' + idx + '"]');
          if (input) input.classList.add('is-error');
        }
      }

      // If no required defined but text section exists, require at least one line
      if (this.state.required.length === 0 && this.state.lines.length > 0) {
        var hasAny = this.state.lines.some(function(l) { return l.trim() !== ''; });
        if (!hasAny) {
          valid = false;
          missing.push('al menos una linea');
        }
      }

      // Check image upload if required
      if (this.config.text && this.config.text.imageRequired && !this.customerImg) {
        valid = false;
        missing.push('imagen');
      }

      if (!valid) {
        this._showMsg('error', 'Completa: ' + missing.join(', '));
      }

      return valid;
    }

    /* ============================================
       UI HELPERS
       ============================================ */
    _showMsg(type, text) {
      this.el.querySelectorAll('[data-pz-msg]').forEach(function(m) { m.classList.remove('is-visible'); });
      var el = this.el.querySelector('[data-pz-msg="' + type + '"]');
      if (el) { el.textContent = text; el.classList.add('is-visible'); }
    }

    _hideMsg() {
      this.el.querySelectorAll('[data-pz-msg]').forEach(function(m) { m.classList.remove('is-visible'); });
    }
  }

  /* ============================================
     AUTO-INIT
     ============================================ */
  function initAll() {
    document.querySelectorAll('[data-product-personalizer]').forEach(function(el) {
      if (!el._pz) el._pz = new ProductPersonalizer(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function(e) {
    var el = e.target.querySelector('[data-product-personalizer]');
    if (el) el._pz = new ProductPersonalizer(el);
  });

})();
